import {
    centsToDollars,
    getCashTotalCurrentBalanceCents,
} from '@fruitful/fruitils';
import { getTotalAmountCentsForRules } from '@fruitful/money-map';
import {
    type HolisticSummary,
    MoneyMapRuleType,
    type MoneyMapRuleWithAccount,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';

import {
    calculateDefaultAnnualEarnings,
    roundUpToNext,
} from '../utils/earningsFormulas';

const processCentsToDollars = (value: number) => {
    // Convert to dollars, round to neat value, ensure no less than min
    return Math.max(roundUpToNext(centsToDollars(value), 10), 0);
};

const getDefaultMonthlyCents = (
    moneyMap: MoneyMapWithRulesAndAccounts,
    ruleType: MoneyMapRuleType,
): number => {
    return getTotalAmountCentsForRules(
        moneyMap.moneyMapRules.filter((rule: MoneyMapRuleWithAccount) => {
            return rule.type === ruleType;
        }),
    );
};

const getDefaultMonthlySavingsCents = (
    moneyMap: MoneyMapWithRulesAndAccounts,
) => {
    // We wanted the default to be the total of all monthly contributions to fruitful goal accounts minus
    // contributions to transfer accounts, external accounts, IM accounts and debt pay down goals,
    // and ignore the target balance aspect (assume goals are constant)
    // However, since we cannot be completely accurate, and since near-ish accuracy would require a lot of
    // complex logic that may be fragile, we will simplify to just setting the default to be:
    // all funds that will be funneled into goals (whether or not the funds stay within fruitful accounts)
    const monthlyIncomeCents = moneyMap.monthlyIncomeCents;
    const monthlyBillsCents = getDefaultMonthlyCents(
        moneyMap,
        MoneyMapRuleType.BILLS,
    );
    const monthlySpendCents = getDefaultMonthlyCents(
        moneyMap,
        MoneyMapRuleType.SPEND,
    );

    return monthlyIncomeCents - monthlyBillsCents - monthlySpendCents;
};

const getDefaultTotalCashCents = (holisticSummary: HolisticSummary): number => {
    // We want to extract the CASH values from the SPEND category (excluding CREDIT), so we call this
    // utility function rather than just returning holisticSummary.spend.totalCurrentBalanceCents
    const spendCashTotalCurrentBalanceCents = getCashTotalCurrentBalanceCents(
        holisticSummary?.spend?.accounts ?? [],
    );
    const saveCashTotalCurrentBalanceCents =
        holisticSummary?.save?.totalCurrentBalanceCents ?? 0;

    return spendCashTotalCurrentBalanceCents + saveCashTotalCurrentBalanceCents;
};

export const useMoneyMapEarningsInputs = ({
    moneyMap,
    holisticSummary,
}: {
    moneyMap: MoneyMapWithRulesAndAccounts;
    holisticSummary: HolisticSummary;
}) => {
    const monthlyIncome = processCentsToDollars(moneyMap.monthlyIncomeCents);

    const monthlySpendingDefault = processCentsToDollars(
        getDefaultMonthlyCents(moneyMap, MoneyMapRuleType.SPEND),
    );

    const monthlySavingsDefault = processCentsToDollars(
        getDefaultMonthlySavingsCents(moneyMap),
    );

    const defaultCash = processCentsToDollars(
        getDefaultTotalCashCents(holisticSummary),
    );
    const minCash = 1000;
    const startingCashDefault = defaultCash > minCash ? defaultCash : minCash; // Default to the sum of member’s Spend and Save holistic categories
    const totalCash = defaultCash > minCash ? defaultCash : minCash * 10; // The sum of member’s Spend and Save holistic categories

    const annualEarningsDefault = calculateDefaultAnnualEarnings(
        monthlySpendingDefault,
        monthlySavingsDefault,
        startingCashDefault,
    );

    return {
        monthlyIncome,
        monthlySpendingDefault,
        monthlySavingsDefault,
        startingCashDefault,
        totalCash,
        annualEarningsDefault,
    };
};
